import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Users, CreditCard, Flame, Cylinder, ShieldAlert, ArrowRight,
  ArrowUpRight, ArrowDownRight, LockOpen, Lock,
} from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell,
} from "recharts";
import api from "@/lib/api";

// Muted design tokens
const INK = "#0f172a";      // slate-900 — primary
const MUTED = "#64748b";    // slate-500
const RULE = "#e2e8f0";     // slate-200
const ACCENT = "#0369a1";   // sky-700 — single subtle accent
const ACCENT_2 = "#f97316"; // gasify flame — used sparingly

const fmtUGX = (n) => `UGX ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const fmtM3  = (n) => `${Number(n || 0).toFixed(3)}`;

// ------------------------------------------------------------------ Card
function Card({ title, testid, children, extra }) {
  return (
    <div className="bg-white border border-slate-200 rounded-sm" data-testid={testid}>
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{title}</div>
        {extra}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

// A single metric row with icon
function Metric({ icon: Icon, label, value, sub, testId }) {
  return (
    <div className="flex items-start gap-3" data-testid={testId}>
      <div className="h-9 w-9 shrink-0 rounded-sm bg-slate-100 grid place-items-center text-slate-500">
        <Icon size={16} strokeWidth={1.9} />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div className="font-heading text-xl font-bold tabular-nums text-slate-900 mt-0.5 leading-none">{value}</div>
        {sub && <div className="text-[11px] text-slate-500 mt-1 tabular-nums">{sub}</div>}
      </div>
    </div>
  );
}

// Metric with 3 buckets (Today / Month / Year)
function ThreeBucket({ label, today, month, year, testid, format }) {
  const f = format || ((v) => v);
  return (
    <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">{label}</div>
      <div className="mt-3 grid grid-cols-3 gap-3">
        {[
          ["Today", today],
          ["Month", month],
          ["Year", year],
        ].map(([k, v]) => (
          <div key={k}>
            <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{k}</div>
            <div className="font-heading font-bold text-slate-900 tabular-nums mt-1 leading-none text-base">{f(v)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ Dashboard
export default function Dashboard() {
  const [stats, setStats] = useState(null);
  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const { data } = await api.get("/dashboard/stats");
        if (mounted) setStats(data);
      } catch { /* noop */ }
    };
    load();
    const t = setInterval(load, 10000);
    return () => { mounted = false; clearInterval(t); };
  }, []);

  const s = stats || {};
  const donutData = [
    { name: "Top-ups",     value: Number(s.revenue_month || 0) },
    { name: "Consumption", value: Number(s.consumption_month_ugx || 0) },
  ];
  const donutColors = [ACCENT, ACCENT_2];

  // 7-day chart: nicer x-axis labels ("Mon", "Tue"…)
  const chartData = (s.topups_7d || []).map((d) => ({
    ...d,
    day: new Date(d.date + "T00:00:00Z").toLocaleDateString(undefined, { weekday: "short" }),
  }));

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-slate-500 font-semibold">Overview</div>
        <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight mt-1">Operations Dashboard</h1>
      </div>

      {/* Row 1 — headline metrics: Households + Valves */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="dashboard-summary-row">
        <Card title="Active households" testid="card-active-households">
          <Metric icon={Users} label="Households with a meter" value={s.active_households ?? "—"} sub={`of ${s.total_households ?? "—"} total`} testId="metric-active-households" />
        </Card>
        <Card title="Valves — open" testid="card-valve-open">
          <Metric icon={LockOpen} label="Meters flowing" value={s.valve_open ?? "—"} sub={`${s.total_meters ?? "—"} meters online`} testId="metric-valve-open" />
        </Card>
        <Card title="Valves — closed" testid="card-valve-closed">
          <Metric icon={Lock} label="Meters shut" value={s.valve_closed ?? "—"} sub={`out of ${s.total_meters ?? "—"}`} testId="metric-valve-closed" />
        </Card>
        <Card title="Alarms open" testid="card-alarms-open">
          <Metric icon={ShieldAlert} label="Unacknowledged" value={s.unacked_alarms ?? "—"} sub={s.unacked_alarms ? "needs attention" : "all clear"} testId="metric-alarms-open" />
        </Card>
      </div>

      {/* Row 2 — Top-ups (today/month/year) and Consumption (m³ + UGX) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ThreeBucket
          label="Top-ups (payments)"
          testid="card-topups-buckets"
          today={s.revenue_today}
          month={s.revenue_month}
          year={s.revenue_year}
          format={fmtUGX}
        />

        {/* Consumption double: m³ on top, UGX below */}
        <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="card-consumption-buckets">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Gas consumption</div>
          <div className="mt-3 grid grid-cols-3 gap-3">
            {[
              ["Today", s.consumption_today_m3, s.consumption_today_ugx],
              ["Month", s.consumption_month_m3, s.consumption_month_ugx],
              ["Year",  s.consumption_year_m3,  s.consumption_year_ugx],
            ].map(([k, m3, ugx]) => (
              <div key={k}>
                <div className="text-[10px] uppercase tracking-widest text-slate-400 font-semibold">{k}</div>
                <div className="font-heading font-bold text-slate-900 tabular-nums mt-1 leading-none text-base">{fmtM3(m3)} <span className="text-[10px] font-normal text-slate-400">m³</span></div>
                <div className="text-[11px] text-slate-500 tabular-nums mt-1">{fmtUGX(ugx)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Row 3 — Alarm log + Donut + Bar chart */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        {/* Alarm log — scrollable 5-row window */}
        <Card
          title="Recent alarms"
          testid="dashboard-alarm-log"
          extra={<Link to="/configurations?tab=alarms" className="text-[11px] text-slate-500 hover:text-slate-800 flex items-center gap-1">All alarms <ArrowRight size={11} /></Link>}
        >
          <div className="max-h-[220px] overflow-y-auto -mx-1" data-testid="alarm-log-scroller">
            {(s.recent_alarms || []).length === 0 && (
              <div className="p-4 text-center text-xs text-slate-400">No alarms recorded.</div>
            )}
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase tracking-widest text-slate-400 sticky top-0 bg-white">
                <tr>
                  <th className="text-left py-2 px-1 font-semibold">Code</th>
                  <th className="text-left py-2 px-1 font-semibold">Time</th>
                  <th className="text-right py-2 px-1 font-semibold">Severity</th>
                </tr>
              </thead>
              <tbody>
                {(s.recent_alarms || []).map((a) => (
                  <tr key={a.id} className="border-t border-slate-100" data-testid="alarm-row">
                    <td className="py-2 px-1 text-slate-800 font-medium">{(a.code || "").replace(/_/g, " ")}</td>
                    <td className="py-2 px-1 text-slate-500 font-mono">{new Date(a.created_at).toLocaleString(undefined, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" })}</td>
                    <td className="py-2 px-1 text-right">
                      <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-semibold uppercase ${
                        a.severity === "CRITICAL" ? "bg-red-50 text-red-700 border border-red-200"
                        : a.severity === "WARNING" ? "bg-amber-50 text-amber-700 border border-amber-200"
                        : "bg-slate-100 text-slate-600 border border-slate-200"}`}>{a.severity}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Donut — consumption vs top-ups this month (UGX) */}
        <Card title="This month · top-ups vs consumption" testid="dashboard-donut-card">
          <div className="h-[220px]" data-testid="dashboard-donut">
            {donutData.some((d) => d.value > 0) ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donutData}
                    dataKey="value"
                    nameKey="name"
                    cx="50%"
                    cy="50%"
                    innerRadius={48}
                    outerRadius={80}
                    stroke="#fff"
                    strokeWidth={2}
                  >
                    {donutData.map((_, i) => <Cell key={i} fill={donutColors[i]} />)}
                  </Pie>
                  <Tooltip formatter={(v) => fmtUGX(v)} contentStyle={{ borderRadius: 2, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-xs text-slate-400">No data this month.</div>
            )}
          </div>
          <div className="mt-3 flex items-center justify-around text-xs">
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-sm" style={{ background: ACCENT }} />
              <div className="text-slate-500">Top-ups</div>
              <div className="text-slate-900 font-semibold tabular-nums">{fmtUGX(s.revenue_month)}</div>
            </div>
            <div className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-sm" style={{ background: ACCENT_2 }} />
              <div className="text-slate-500">Consumption</div>
              <div className="text-slate-900 font-semibold tabular-nums">{fmtUGX(s.consumption_month_ugx)}</div>
            </div>
          </div>
        </Card>

        {/* Bar chart — top-ups last 7 days */}
        <Card title="Top-ups · last 7 days" testid="dashboard-bar-card">
          <div className="h-[220px]" data-testid="dashboard-bar-chart">
            {chartData.length ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                  <CartesianGrid stroke={RULE} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="day" stroke={MUTED} tick={{ fontSize: 10 }} tickLine={false} axisLine={{ stroke: RULE }} />
                  <YAxis stroke={MUTED} tick={{ fontSize: 10 }} tickLine={false} axisLine={{ stroke: RULE }} tickFormatter={(v) => v >= 1000 ? `${(v/1000).toFixed(0)}k` : v} />
                  <Tooltip
                    formatter={(v) => fmtUGX(v)}
                    labelFormatter={(l, p) => (p && p[0]?.payload?.date) || l}
                    contentStyle={{ borderRadius: 2, fontSize: 12 }}
                  />
                  <Bar dataKey="amount" fill={INK} radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-xs text-slate-400">No top-ups yet.</div>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
}
